import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  MailCheck,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { AuthLayout } from "../components/AuthLayout";
import { Button, ButtonLink, Input, Label } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { PasswordResetValidation } from "../types/api";

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

export function PasswordResetRequestPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Informe o e-mail cadastrado.");
      return;
    }

    setLoading(true);
    try {
      await api.requestPasswordReset(email);
      setSent(true);
    } catch (requestError) {
      setError(
        errorMessage(
          requestError,
          "Não foi possível solicitar a recuperação. Tente novamente.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      {sent ? (
        <div className="access-status access-status--success">
          <span className="access-status-icon"><MailCheck /></span>
          <p className="eyebrow">Verifique seu e-mail</p>
          <h2>Instruções enviadas</h2>
          <p>
            Se <strong>{email}</strong> estiver cadastrado e ativo, enviaremos
            um link para criar uma nova senha.
          </p>
          <div className="access-notice">
            <ShieldCheck />
            <span>O link é pessoal, de uso único e expira em 60 minutos.</span>
          </div>
          <ButtonLink to="/login" variant="secondary" size="lg" className="w-full">
            <ArrowLeft />
            Voltar ao login
          </ButtonLink>
        </div>
      ) : (
        <>
          <ButtonLink to="/login" variant="ghost" size="sm" className="auth-back-link">
            <ArrowLeft />
            Voltar
          </ButtonLink>
          <p className="eyebrow">Recuperação de acesso</p>
          <h2>Esqueceu sua senha?</h2>
          <p className="login-subtitle">
            Informe o e-mail cadastrado. Enviaremos um link seguro para você
            criar uma nova senha.
          </p>

          <form onSubmit={submit} className="login-form">
            <div>
              <Label htmlFor="password-reset-email">E-mail cadastrado</Label>
              <div className="input-with-icon">
                <Mail />
                <Input
                  id="password-reset-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="nome@suaempresa.com.br"
                  aria-invalid={Boolean(error)}
                />
              </div>
            </div>
            {error && <p className="auth-form-error" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={loading} className="w-full">
              Enviar link de recuperação
              {!loading && <ArrowRight />}
            </Button>
          </form>

          <div className="access-notice access-notice--muted">
            <ShieldCheck />
            <span>Por segurança, não informamos se um e-mail está cadastrado.</span>
          </div>
        </>
      )}
    </AuthLayout>
  );
}

export function PasswordResetConfirmPage() {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
    [],
  );
  const [validation, setValidation] = useState<PasswordResetValidation | null>(null);
  const [checking, setChecking] = useState(Boolean(token));
  const [invalidMessage, setInvalidMessage] = useState(
    token ? "" : "O link de recuperação está incompleto.",
  );
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) return undefined;

    api.validatePasswordResetToken(token)
      .then((result) => {
        if (active) setValidation(result);
      })
      .catch((validationError) => {
        if (active) {
          setInvalidMessage(
            errorMessage(
              validationError,
              "Este link de recuperação é inválido ou expirou.",
            ),
          );
        }
      })
      .finally(() => {
        if (active) setChecking(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const rules = {
    length: password.length >= 8 && password.length <= 50,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const passwordIsValid = Object.values(rules).every(Boolean);
  const passwordsMatch = password.length > 0 && password === confirmation;

  async function resetPassword(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!passwordIsValid) {
      setError("Crie uma senha que atenda a todos os requisitos.");
      return;
    }
    if (!passwordsMatch) {
      setError("A confirmação da senha não coincide.");
      return;
    }

    setLoading(true);
    try {
      await api.confirmPasswordReset(token, password);
      setCompleted(true);
    } catch (resetError) {
      setError(
        errorMessage(
          resetError,
          "Não foi possível redefinir sua senha. Solicite um novo link.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      {checking ? (
        <div className="access-status">
          <span className="loader-orbit" />
          <h2>Validando seu link</h2>
          <p>Estamos verificando a segurança da solicitação.</p>
        </div>
      ) : invalidMessage ? (
        <div className="access-status access-status--error">
          <span className="access-status-icon"><XCircle /></span>
          <p className="eyebrow">Link indisponível</p>
          <h2>Não foi possível continuar</h2>
          <p>{invalidMessage}</p>
          <ButtonLink to="/recuperar-senha" size="lg" className="w-full">
            Solicitar novo link
          </ButtonLink>
          <ButtonLink to="/login" variant="ghost" size="lg" className="w-full">
            Voltar ao login
          </ButtonLink>
        </div>
      ) : completed ? (
        <div className="access-status access-status--success">
          <span className="access-status-icon"><CheckCircle2 /></span>
          <p className="eyebrow">Senha atualizada</p>
          <h2>Seu acesso está pronto</h2>
          <p>A nova senha foi salva e eventuais bloqueios de login foram removidos.</p>
          <ButtonLink to="/login" size="lg" className="w-full">
            Entrar no helpdesk
            <ArrowRight />
          </ButtonLink>
        </div>
      ) : (
        <>
          <p className="eyebrow">Recuperação segura</p>
          <h2>Crie uma nova senha</h2>
          <p className="login-subtitle">
            Redefina o acesso de <strong>{validation?.email}</strong>.
          </p>

          <form onSubmit={resetPassword} className="login-form">
            <div>
              <Label htmlFor="reset-password">Nova senha</Label>
              <div className="input-with-icon">
                <KeyRound />
                <Input
                  id="reset-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Digite uma senha segura"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Ocultar senha" : "Exibir senha"}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="password-rules" aria-label="Requisitos da senha">
              <span className={rules.length ? "is-valid" : ""}><Check /> 8 a 50 caracteres</span>
              <span className={rules.upper ? "is-valid" : ""}><Check /> Uma letra maiúscula</span>
              <span className={rules.lower ? "is-valid" : ""}><Check /> Uma letra minúscula</span>
              <span className={rules.number ? "is-valid" : ""}><Check /> Um número</span>
            </div>

            <div>
              <Label htmlFor="reset-password-confirmation">Confirme a senha</Label>
              <div className="input-with-icon">
                <KeyRound />
                <Input
                  id="reset-password-confirmation"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  placeholder="Digite novamente"
                  aria-invalid={confirmation.length > 0 && !passwordsMatch}
                />
              </div>
              {confirmation.length > 0 && (
                <p className={`password-match ${passwordsMatch ? "is-valid" : ""}`}>
                  {passwordsMatch ? <CheckCircle2 /> : <XCircle />}
                  {passwordsMatch ? "As senhas coincidem" : "As senhas ainda não coincidem"}
                </p>
              )}
            </div>

            {error && <p className="auth-form-error" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={loading} className="w-full">
              Salvar nova senha
              {!loading && <ArrowRight />}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
